import { Chip } from '@mui/material';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useTranslation } from 'react-i18next';
import type { ReactElement } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TaskPriority } from '@/hooks/useTasks';

type ChipColor = 'default' | 'primary' | 'warning' | 'error';
type ChipVariant = 'filled' | 'outlined';

interface PriorityConfig {
  readonly color: ChipColor;
  readonly variant: ChipVariant;
  readonly icon: ReactElement;
  readonly labelKey: string;
  readonly sx?: SxProps<Theme>;
}

// Each priority maps to a colour AND an icon, so urgency is conveyed by more
// than colour alone (accessibility for low-vision / colour-blind users).
// Higher priorities use "filled" variant so they stand out immediately in a list.
const PRIORITY_CONFIG: Record<TaskPriority, PriorityConfig> = {
  low: {
    color: 'default',
    variant: 'outlined',
    icon: <LowPriorityIcon fontSize="small" />,
    labelKey: 'myTasks.priorityLow',
  },
  normal: {
    color: 'primary',
    variant: 'outlined',
    icon: <DragHandleIcon fontSize="small" />,
    labelKey: 'myTasks.priorityNormal',
  },
  high: {
    color: 'warning',
    variant: 'filled',
    icon: <KeyboardArrowUpIcon fontSize="small" />,
    labelKey: 'myTasks.priorityHigh',
    sx: { fontWeight: 700 },
  },
  urgent: {
    color: 'error',
    variant: 'filled',
    icon: <PriorityHighIcon fontSize="small" />,
    labelKey: 'myTasks.priorityUrgent',
    sx: { fontWeight: 700, letterSpacing: '0.03em' },
  },
};

export interface PriorityChipProps {
  readonly priority: TaskPriority;
  readonly size?: 'small' | 'medium';
}

/**
 * Renders a task priority as a coloured, icon-bearing chip. Higher priorities
 * use "filled" chips with bold text so urgency reads at a glance without
 * relying on colour alone. Low and normal priorities use the subtler "outlined"
 * style so urgent tasks immediately stand out in a crowded task list.
 */
export function PriorityChip({ priority, size = 'small' }: PriorityChipProps) {
  const { t } = useTranslation('workorders');
  const config = PRIORITY_CONFIG[priority];
  const label = t(config.labelKey);

  return (
    <Chip
      data-testid="priority-chip"
      data-priority={priority}
      size={size}
      icon={config.icon}
      label={label}
      color={config.color}
      variant={config.variant}
      sx={config.sx}
      aria-label={label}
    />
  );
}
